import { SearchHighlight } from '../types/string.types';
export const splitWith = (
  data?: null | string | string[],
  symbol: RegExp | string = /,/,
): string[] => {
  if (!data) return [];

  const s = Array.isArray(data) ? data.join(',') : data;

  return s
    .split(symbol)
    .map((c): string => c.trim())
    .filter(Boolean);
};

/**
 * The other direction of {@link splitWith}: a list as one line of text.
 *
 * For the boundary between a record that holds a list and a field that edits it
 * as free text — a form seeded with the list itself hands an array to a control
 * that can only show one value, and what it shows is the first.
 *
 * Empty entries are dropped, as {@link splitWith} drops them, so a value can
 * cross both ways and come back the same.
 * @param {null | string | string[] | undefined} data The list, or a value that is already one line.
 * @param {string} separator What to put between the entries.
 * @returns {string} The entries on one line; empty when there are none.
 */
export const joinWith = (data?: null | string | string[], separator = ', '): string => {
  if (!data) return '';

  return Array.isArray(data) ? data.filter(Boolean).join(separator) : data;
};

/**
 * Text for an SV cell tooltip: the data-view detail when the backend sent one,
 * otherwise the calculation formula. Escaped `\n` sequences become real line
 * breaks so the native tooltip renders one line per entry.
 * @param {unknown} calculation The calculation formula of the cell.
 * @param {unknown} calculationDetail The data-view detail of the cell.
 * @returns {unknown} The text to show in the tooltip.
 */
export const getCalculationTooltip = (calculation: unknown, calculationDetail: unknown): unknown =>
  typeof calculationDetail === 'string' && calculationDetail.trim()
    ? calculationDetail.replace(/\\r\\n|\\n|\r\n/g, '\n')
    : calculation;

const toSplitInput = (v: unknown): null | string => (v == null ? null : String(v));

export const split = (v: unknown): string[] => splitWith(toSplitInput(v));

export const splitMap = (v: unknown, map: (s: string) => string): string[] => {
  const normalized = toSplitInput(v);

  if (normalized == null) return [];
  if (Array.isArray(normalized)) return normalized.map((x): string => map(String(x)));

  return splitWith(map(normalized));
};

const DIACRITIC_MARKS = /[̀-ͯ]/g;

const normalizeChar = (char: string): string => {
  const stripped = char.normalize('NFD').replace(DIACRITIC_MARKS, '').toLowerCase();
  return stripped.length === 1 ? stripped : char;
};

/**
 * Lowercases and strips diacritics, one character at a time so the result keeps
 * the same length as the input and positions stay comparable.
 * @param {null | string} value The text to normalise.
 * @returns {string} A comparable form, usable on both sides of a search.
 */
export const normalizeForSearch = (value?: null | string): string =>
  value ? value.split('').map(normalizeChar).join('') : '';

/**
 * Locates a search term in a text, ignoring case and diacritics.
 *
 * The index is found in the normalised text and used to cut the original, which
 * is what {@link normalizeForSearch} preserves the length for — so searching
 * `dodatecna` marks `Dodatečná`, accents and all.
 * @param {null | string} value The text as it is shown.
 * @param {null | string} search What the user typed.
 * @returns {SearchHighlight} The text before the hit, the hit, and the rest.
 */
export const splitBySearch = (value?: null | string, search?: null | string): SearchHighlight => {
  const text = value ?? '';
  const term = normalizeForSearch(search);
  const index = term ? normalizeForSearch(text).indexOf(term) : -1;

  if (index === -1) return { after: '', before: text, match: '' };

  return {
    after: text.slice(index + term.length),
    before: text.slice(0, index),
    match: text.slice(index, index + term.length),
  };
};
